import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ButtonDropdownMultiple, ButtonDropdownMultipleProps } from '../../../ButtonDropdownMultiple'
import { IconButton } from '../../../IconButton'
import { NavItem } from '../../../NavList'
import { SkeletonIconButton } from '../../../SkeletonIconButton'
import { Tooltip } from '../../../Tooltip'
import { TableToggleableColumn } from '../../types/column'
import * as Styled from './styles'

interface TableColumnsFilteringProps {
  columns: TableToggleableColumn[]
  isLoading?: boolean
  hiddenColumnsIds: string[]
  onSelect: (id: string, checked?: boolean) => void
  // Where the dropdown opens. Default: `top-start`, which suits the bottom bar of the table.
  placement?: ButtonDropdownMultipleProps['placement']
  // Adds the spacing needed next to the pagination. Turn it off when rendered somewhere else.
  withSpacing?: boolean
}

export const TableColumnsFiltering = ({
  columns,
  isLoading,
  hiddenColumnsIds,
  onSelect,
  placement = 'top-start',
  withSpacing = true,
}: TableColumnsFilteringProps): ReactElement => {
  const { t } = useTranslation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const toggleColumnsLabel = t('table_column_toggler.toggle_columns')

  const dropdownItems = useMemo(
    (): NavItem[] =>
      columns.map(({ id, name }) => ({
        children: name,
        id,
        value: id,
      })),
    [columns],
  )

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownOpen(true)
  }, [])

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const selectedValues = useMemo(
    () => dropdownItems.filter(({ value }) => !hiddenColumnsIds.includes(value)).map(({ value }) => value),
    [dropdownItems, hiddenColumnsIds],
  )

  const handleSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedIds: string[], itemId?: string, itemChecked?: boolean) => {
      if (itemId) {
        onSelect(itemId, itemChecked)
      }
    },
    [onSelect],
  )

  return (
    <Styled.TableFilteringWrapper withSpacing={withSpacing}>
      {isLoading ? (
        <SkeletonIconButton size="md" />
      ) : (
        <Tooltip forceHidden={isDropdownOpen} label={toggleColumnsLabel} placement="top">
          <ButtonDropdownMultiple
            items={dropdownItems}
            selectedValues={selectedValues}
            onClose={handleDropdownClose}
            onOpen={handleDropdownOpen}
            onSelect={handleSelect}
            placement={placement}
            size="md"
            triggerElement={<IconButton aria-label={toggleColumnsLabel} icon="columnsCog" size="md" variant="tonal" />}
          />
        </Tooltip>
      )}
    </Styled.TableFilteringWrapper>
  )
}
